import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Pencil, X, Check } from 'lucide-react';
import { useUpdateApartmentDetails } from '../../hooks/useApartments';
import type { Apartment } from '../../types/apartment';

const schema = z.object({
  name: z.string().min(3, 'forms:nameTooShort'),
  description: z.string().min(10, 'forms:descriptionTooShort'),
  bookingFlowMode: z.preprocess(
    (val) => (val === '' ? undefined : val),
    z.enum(['IMMEDIATE_PAYMENT', 'DEFERRED_PAYMENT', 'DEFERRED_BANK_TRANSFER']).optional()
  ),
});

type FormData = z.infer<typeof schema>;

interface BasicInfoSectionProps {
  readonly apartment: Apartment;
}

export const BasicInfoSection = ({ apartment }: BasicInfoSectionProps) => {
  const { t } = useTranslation(['common', 'forms', 'errors', 'pages']);
  const [isEditing, setIsEditing] = useState(false);
  const updateMutation = useUpdateApartmentDetails();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: apartment.name,
      description: apartment.description,
      bookingFlowMode: apartment.bookingFlowMode,
    },
  });

  const handleEdit = () => {
    reset({
      name: apartment.name,
      description: apartment.description,
      bookingFlowMode: apartment.bookingFlowMode,
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset({
      name: apartment.name,
      description: apartment.description,
      bookingFlowMode: apartment.bookingFlowMode,
    });
  };

  const onSubmit = async (data: FormData) => {
    try {
      await updateMutation.mutateAsync({
        id: apartment.id,
        data: {
          name: data.name,
          description: data.description,
          bedrooms: apartment.details.bedrooms,
          bathrooms: apartment.details.bathrooms,
          maxGuests: apartment.details.maxGuests,
          sizeSquareMeters: apartment.details.sizeSquareMeters,
          bookingFlowMode: data.bookingFlowMode,
        },
      });
      setIsEditing(false);
    } catch {
      // error shown inline via updateMutation.isError
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{t('forms:basicInformation')}</h2>
        {!isEditing && (
          <button
            onClick={handleEdit}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
            aria-label={t('common:edit')}
          >
            <Pencil size={16} />
          </button>
        )}
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:name')}</label>
            <input
              {...register('name')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:description')}</label>
            <textarea
              {...register('description')}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.description && <p className="text-sm text-red-600 mt-1">{errors.description.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:bookingFlowMode')}</label>
            <select
              {...register('bookingFlowMode')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">—</option>
              <option value="IMMEDIATE_PAYMENT">{t('forms:bookingFlowMode_immediatePayment')}</option>
              <option value="DEFERRED_PAYMENT">{t('forms:bookingFlowMode_deferredPayment')}</option>
              <option value="DEFERRED_BANK_TRANSFER">{t('forms:bookingFlowMode_deferredBankTransfer')}</option>
            </select>
          </div>
          {updateMutation.isError && (
            <p className="text-sm text-red-600">{t('errors:somethingWentWrong')}</p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex items-center gap-2 py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 min-h-[44px]"
            >
              <Check size={16} />
              {updateMutation.isPending ? t('common:saving') : t('common:save')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center gap-2 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium min-h-[44px]"
            >
              <X size={16} />
              {t('common:cancel')}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-gray-500">{t('forms:name')}</p>
            <p className="text-gray-900">{apartment.name}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">{t('forms:description')}</p>
            <p className="text-gray-700 whitespace-pre-wrap">{apartment.description}</p>
          </div>
          {apartment.icalUrl && (
            <div>
              <p className="text-sm font-medium text-gray-500">{t('pages:icalFeedOutboundUrl')}</p>
              <input
                type="url"
                value={apartment.icalUrl}
                readOnly
                onFocus={(e) => e.target.select()}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-600 mt-1"
              />
            </div>
          )}
          {apartment.bookingFlowMode && (
            <div>
              <p className="text-sm font-medium text-gray-500">{t('forms:bookingFlowMode')}</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                apartment.bookingFlowMode === 'IMMEDIATE_PAYMENT'
                  ? 'bg-green-100 text-green-800'
                  : apartment.bookingFlowMode === 'DEFERRED_BANK_TRANSFER'
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {(() => {
                  switch (apartment.bookingFlowMode) {
                    case 'IMMEDIATE_PAYMENT': return t('forms:bookingFlowMode_immediatePayment');
                    case 'DEFERRED_PAYMENT': return t('forms:bookingFlowMode_deferredPayment');
                    case 'DEFERRED_BANK_TRANSFER': return t('forms:bookingFlowMode_deferredBankTransfer');
                  }
                })()}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BasicInfoSection;
